import React, {
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

import TextInput from 'components/Common/Form/Input/TextInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import BodyText from 'components/Luxkit/Typography/BodyText'
import InputText from 'components/Luxkit/Typography/InputText'
import SelectItemModal from 'tripPlanner/components/TripModal/Templates/SelectItemModal'
import { MAX_TRIP_NAME_CHARS } from 'tripPlanner/config'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { FullTrip } from 'tripPlanner/types/common'

interface Props {
  template: FullTrip
}

const CharCount = styled(InputText)`
  position: absolute;
  bottom: -4px;
  transform: translateY(100%);
  right: 0px;
`

function CopyTemplate({ template }: Props) {
  const openModal = useOpenModal()
  const inputRef = useRef<HTMLInputElement>(null)

  const { closeModal, goBack } = useModalCallbacks()

  const [name, setName] = useState(template.name)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  const onFormSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      if (name.length > 0) {
        openModal(
          SelectItemModal,
          {
            template,
            name,
          },
          { height: 'full' },
        )
      }
    },
    [name, openModal, template],
  )

  return (
    <form onSubmit={onFormSubmit}>
      <ModalHeader
        title="Create new trip"
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={24}>
            <BodyText colour="neutral-three" variant="medium">
              You can either keep the current trip name or change it to
              something else.
            </BodyText>
            <TextInput
              label="Name your trip"
              ref={inputRef}
              width="100%"
              onChange={(e) => {
                setName(e.currentTarget.value)
              }}
              manualError
              maxLength={MAX_TRIP_NAME_CHARS}
              data-testid="trip-popup-trip-input"
              required
              value={name}
            >
              <CharCount variant="caption" colour="neutral-three">
                {name.length}/{MAX_TRIP_NAME_CHARS}
              </CharCount>
            </TextInput>
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Continue',
          disabled: name.length === 0,
          type: 'submit',
        }}
      />
    </form>
  )
}

export default CopyTemplate
